import type {
  PaymentAccountStatus,
  PaymentProviderAccount,
  PaymentsDispute,
  PaymentsPayoutDetail,
  PaymentsPayoutSchedule,
  PaymentsPayoutScheduleInput,
  PaymentsSummary,
} from "schemas/generated/paymentProvider";
import type {
  AutomaticTaxStatus,
  PaymentsOnboardingSurface,
} from "schemas/paymentProvider";
import type { RequestFn } from "./request";

export function createPaymentProviderAccountClient({
  request,
}: {
  request: RequestFn;
}) {
  return {
    setUp: ({
      projectId,
      agentSessionId,
      returnTo,
    }: {
      projectId: string;
      // The caller's open chat and mini-app, round-tripped through Stripe so
      // the onboarding bounce can land the merchant back in them.
      agentSessionId: string | null;
      returnTo: PaymentsOnboardingSurface;
    }) => {
      return request<{ onboardingUrl: string }>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/payment-provider-account/setup`,
        query: { agentSessionId: agentSessionId ?? undefined, returnTo },
      });
    },
    createFinishSetupLink: ({
      projectId,
      agentSessionId,
      returnTo,
    }: {
      projectId: string;
      agentSessionId: string | null;
      returnTo: PaymentsOnboardingSurface;
    }) => {
      return request<{ url: string }>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/payment-provider-account/finish-setup-link`,
        query: { agentSessionId: agentSessionId ?? undefined, returnTo },
      });
    },
    createDashboardLink: ({ projectId }: { projectId: string }) => {
      return request<{ url: string }>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/payment-provider-account/dashboard-link`,
      });
    },
    getAccount: ({ projectId }: { projectId: string }) => {
      return request<{
        account: PaymentProviderAccount | null;
        status: PaymentAccountStatus;
      }>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/payment-provider-account`,
      });
    },
    getTaxStatus: ({ projectId }: { projectId: string }) => {
      return request<{ taxStatus: AutomaticTaxStatus }>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/payment-provider-account/tax-status`,
      });
    },
    getSummary: ({ projectId }: { projectId: string }) => {
      return request<PaymentsSummary>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/payment-provider-account/summary`,
      });
    },
    getPayout: ({
      projectId,
      payoutId,
    }: {
      projectId: string;
      payoutId: string;
    }) => {
      return request<PaymentsPayoutDetail>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/payment-provider-account/payouts/${encodeURIComponent(payoutId)}`,
      });
    },
    findDisputes: ({ projectId }: { projectId: string }) => {
      return request<{ disputes: PaymentsDispute[] }>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/payment-provider-account/disputes`,
      });
    },
    updatePayoutSchedule: ({
      projectId,
      schedule,
    }: {
      projectId: string;
      schedule: PaymentsPayoutScheduleInput;
    }) => {
      return request<{ payoutSchedule: PaymentsPayoutSchedule }>({
        method: "PUT",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/payment-provider-account/payout-schedule`,
        body: schedule,
      });
    },
    disconnect: ({ projectId }: { projectId: string }) => {
      return request<{ success: boolean }>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/payment-provider-account/disconnect`,
      });
    },
  };
}
